import React, { useContext, useMemo } from 'react';
import { Typography, Box } from '@mui/material';
import { Translate, LocalizeContext } from 'react-localize-redux';
import { DiagnosisTableData, DiagnosisTableRow } from '../types';
import DiagnosisRowsTable from './DiagnosisRowsTable';
import ExportTableButton from '../common/ExportTableButton';
import { ExportColumn } from '../../../../utils/csvDownload';

interface DiagnosisTableProps {
  data: DiagnosisTableData;
  title: string;
}

/**
 * SHER-306 / SHER-583. The outpatient ICD-11 panel: "Patients by recorded diagnosis (ICD-11)".
 *
 * The table itself is `DiagnosisRowsTable`, shared with the inpatient discharge panel, because
 * the client asked on 2026-09-18 for the two to stop looking like different tables. What stays
 * here is what genuinely differs: this panel's title, its subtitle, and its empty state.
 *
 * No `useState`/`useEffect` any more. The rows arrive assembled from `transformUniquePatientData`
 * rather than as index-aligned arrays this component had to re-zip on every change of either
 * `data` or the patient store.
 *
 * SHER-905 makes this the first table to opt into the shared CSV export. The opt-in is one
 * column list and one button — the whole point of the enabler — and it is wired HERE rather
 * than inside `DiagnosisRowsTable` on purpose: the inpatient discharge panel shares that
 * component but has a Discharges column this one does not, so an export declared in the shared
 * table would be wrong for one of its two callers.
 */
const DiagnosisTable: React.FC<DiagnosisTableProps> = ({ data, title }) => {
  const { translate } = useContext(LocalizeContext);
  const rows = data?.rows ?? [];

  // The columns the table displays, in display order. Two deliberate differences from the
  // rendered cell, both following SHER-462's rule that an export carries the full value and
  // not the abbreviation the layout needed:
  //   - every service, not the first three `DiagnosisRowsTable` has room for;
  //   - an empty cell where the API sent no patient count, rather than the em dash shown on
  //     screen, because a spreadsheet can sum a blank column and cannot sum a dash.
  const columns = useMemo<ExportColumn<DiagnosisTableRow>[]>(() => [
    { header: String(translate('hospital.analytics.diagnosis_table.diagnosis')), value: (row) => row.name },
    { header: String(translate('hospital.analytics.diagnosis_table.code')), value: (row) => row.code },
    { header: String(translate('hospital.analytics.diagnosis_table.female')), value: (row) => row.female },
    { header: String(translate('hospital.analytics.diagnosis_table.male')), value: (row) => row.male },
    { header: String(translate('hospital.analytics.diagnosis_table.total')), value: (row) => row.patients },
    { header: String(translate('hospital.analytics.diagnosis_table.services')), value: (row) => row.departments.join(', ') },
  ], [translate]);

  if (rows.length === 0) {
    return (
      <Box p={3}>
        <Typography variant="subtitle1">No diagnosis data available</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{title}</Typography>
        <ExportTableButton title={title} rows={rows} columns={columns} />
      </Box>
      {/* SHER-306: the client asked for this line on 2026-09-18. It states the population the
          table reports, which is what stops the figures being read as consultations. */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        <Translate id="hospital.analytics.outpatient_diagnoses.subtitle" />
      </Typography>
      <DiagnosisRowsTable rows={rows} />
    </Box>
  );
};

export default DiagnosisTable;
